/**
 * @fileoverview Contains abstractions for using Scrmajet under an iframe.
 */

import { VortexController } from "@/controller/index";
import type { VortexClient } from "@client/index";
import type { VortexEvent, VortexEvents } from "@client/events";
import { VORTEXCLIENT, VORTEXFRAME } from "@/symbols";
function createFrameId() {
	return `${Array(8)
		.fill(0)
		.map(() => Math.floor(Math.random() * 36).toString(36))
		.join("")}`;
}

/**
 * An abstraction over proxy iframe creation, which lets you manage instances of Vortex and not have to worry about the proxy internals, since everything you need is already proxified.
 *
 * @example
 * ```typescript
 * const { VortexController } = $vortexLoadController();
 * const vortex = new VortexController({ prefix: "/vortex/" });
 * await vortex.init();
 *
 * const frame = vortex.createFrame();
 * document.body.appendChild(frame.frame);
 *
 * // Navigate to a URL
 * frame.go("https://example.com");
 *
 * // Listen for proxified navigation events
 * frame.addEventListener("urlchange", (e) => {
 *   console.log("URL changed to:", e.url);
 * });
 *
 * // Go back
 * frame.back();
 * // Go forward
 * frame.forward();
 * // Reload page
 * frame.reload();
 * ```
 */
export class VortexFrame extends EventTarget {
	/**
	 * Create a VortexFrame instance. You likely won't need to interact the {@link VortexFrame.constructor | constructor} directly.
	 * You can instead use {@link VortexController.createFrame} on your existing `VortexController`.
	 *
	 * @param controller The `VortexController` instance that manages this frame with.
	 * @param frame The frame to be controlled for you under Vortex.
	 */
	constructor(
		private controller: VortexController,
		public frame: HTMLIFrameElement
	) {
		super();
		frame.name = createFrameId();
		frame[VORTEXFRAME] = this;
	}

	/**
	 * Returns the {@link VortexClient} instance running inside the iframe's contentWindow.
	 *
	 * @returns The `VortexClient` instance.
	 */
	get client(): VortexClient {
		return this.frame.contentWindow.window[VORTEXCLIENT];
	}

	/**
	 * Returns the proxified URL.
	 *
	 * @returns The proxified URL.
	 */
	get url(): URL {
		return this.client.url;
	}

	/**
	 * Navigates the iframe to a new URL under Vortex.
	 *
	 * @example
	 * ```typescript
	 * frame.go("https://example.net");
	 * ```
	 *
	 * @param url A real URL to navigate to
	 */
	go(url: string | URL) {
		if (url instanceof URL) url = url.toString();

		dbg.log("navigated to", url);

		this.frame.src = this.controller.encodeUrl(url);
	}

	/**
	 * Goes backwards in the browser history.
	 */
	back() {
		this.frame.contentWindow?.history.back();
	}

	/**
	 * Goes forward in the browser history.
	 */
	forward() {
		this.frame.contentWindow?.history.forward();
	}

	/**
	 * Reloads the iframe.
	 */
	reload() {
		this.frame.contentWindow?.location.reload();
	}

	/**
	 * Binds event listeners to listen for proxified navigation events in Vortex.
	 *
	 * @example
	 * ```typescript
	 * // Listen for URL changes
	 * frame.addEventListener("urlchange", (event) => {
	 *   console.log("URL changed:", event.url);
	 *   document.title = event.url; // Update page title
	 * });
	 *
	 * // Listen for navigation events
	 * frame.addEventListener("navigate", (event) => {
	 *   console.log("Navigating to:", event.url);
	 * });
	 * ```
	 *
	 * @param type Type of event to listen for.
	 * @param listener Event listener to dispatch.
	 * @param options Options for the event listener.
	 */
	addEventListener<K extends keyof VortexEvents>(
		type: K,
		listener: (event: VortexEvents[K]) => void,
		options?: boolean | AddEventListenerOptions
	): void {
		super.addEventListener(type, listener as EventListener, options);
	}
}
